import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

export type To = string | { pathname?: string; search?: string; hash?: string };
type NavigateMethod = (to: To, ...rest: unknown[]) => void;

interface GuardedNavigator {
  push: NavigateMethod;
  replace: NavigateMethod;
}

/** The pathname of a router target, with no query and no hash. */
export function targetPathname(to: To): string {
  const path = typeof to === 'string' ? to : (to.pathname ?? '');
  return (path.split('?')[0] ?? '').split('#')[0] ?? '';
}

/** The query string of a router target (no `?`), with no hash. Empty when it has none. */
export function targetSearch(to: To): string {
  if (typeof to !== 'string') {
    return (to.search ?? '').replace(/^\?/, '');
  }
  const noHash = to.split('#')[0] ?? '';
  const index = noHash.indexOf('?');
  return index === -1 ? '' : noHash.slice(index + 1);
}

/** The default rule: a target with no path or with the path of this page stays on the page. */
export function isSamePathname(to: To): boolean {
  const target = targetPathname(to);
  // The router gives a full path (with the `/app` base), as `window.location.pathname` is.
  return target === '' || target === window.location.pathname;
}

/**
 * The leave guard of the song page (design song-page f4). While `when` is true:
 * - a router navigation to another page (a menu item, a crumb, a link) waits; `isBlocked` turns
 *   true and the page shows its dialog. `proceed` goes on, `cancel` stays.
 * - a reload or a closed tab gets the browser `beforeunload` prompt.
 * A navigation inside the page (`?tab=`, `?view=`) is not a leave and goes on at once. A page
 * whose query names what it shows (the Releases song page: `?catalogItemUuid=`) gives its own
 * `isSamePage` rule.
 *
 * The app uses `BrowserRouter`, which has no `useBlocker` (that needs a data router), so the guard
 * wraps `push` and `replace` of the router's navigator while it is on. The browser Back button is
 * not guarded: `popstate` cannot wait for a dialog.
 */
export function useLeaveGuard(when: boolean, isSamePage: (to: To) => boolean = isSamePathname) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const isSamePageRef = useRef(isSamePage);
  isSamePageRef.current = isSamePage;

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    const nav = navigator as unknown as GuardedNavigator;
    const { push, replace } = nav;
    const guard =
      (method: NavigateMethod): NavigateMethod =>
      (to, ...rest) => {
        if (isSamePageRef.current(to)) {
          method.call(nav, to, ...rest);
          return;
        }
        setPending(() => () => method.call(nav, to, ...rest));
      };
    nav.push = guard(push);
    nav.replace = guard(replace);
    return () => {
      nav.push = push;
      nav.replace = replace;
    };
  }, [navigator, when]);

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Old browsers show the prompt only when `returnValue` is set.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  const proceed = useCallback(() => {
    const go = pending;
    setPending(null);
    go?.();
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return { isBlocked: pending !== null, proceed, cancel };
}
